import { DEG } from '../math';
import { scaleParamValues } from '../params/scale';
import { TEXT_LAYER_PARAMS } from '../params/textLayer';
import { contoursToPathData } from '../text/flatten';
import { getTechnique } from '../../techniques/registry';
import { TEXT_TECHNIQUE_ID } from '../../store/layer';
import type { StudioDocument } from '../../store/studioStore';
import type { RenderedDocument } from './renderDocument';

export interface SvgResult {
  svg: string;
  /** Names of techniques that produced no vector output. */
  rasterOnly: string[];
}

const escapeXml = (s: string): string =>
  s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[c] ?? c);

/**
 * Builds an SVG from a rendered document.
 *
 * Only techniques that implement `toSVG` contribute vectors; the rest are reported
 * back so the UI can say plainly what is missing rather than silently exporting an
 * incomplete file. A flow field is genuinely raster by nature — two million line
 * segments would be a file no editor could open — so it has no vector form, and
 * saying so is more useful than pretending otherwise.
 */
export function documentToSvg(doc: StudioDocument, rendered: RenderedDocument, scale: number): SvgResult {
  const { width, height } = rendered;
  const parts: string[] = [];
  const rasterOnly: string[] = [];

  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" ` +
      `viewBox="0 0 ${width} ${height}" shape-rendering="geometricPrecision">`,
  );
  parts.push(backgroundSvg(doc, width, height));

  const textLayer = doc.layers.find((l) => l.techniqueId === TEXT_TECHNIQUE_ID);
  const textLayerValues = textLayer ? scaleParamValues(TEXT_LAYER_PARAMS, textLayer.values, scale) : {};

  for (const layer of doc.layers) {
    if (!layer.visible || layer.opacity <= 0) continue;

    const blend = layer.blendMode === 'normal' ? '' : `mix-blend-mode:${layer.blendMode};`;
    const style = `opacity:${layer.opacity};${blend}`;
    const open = `<g id="${escapeXml(layer.id)}" data-name="${escapeXml(layer.name)}" style="${style}">`;

    if (layer.techniqueId === TEXT_TECHNIQUE_ID) {
      const settings = rendered.textSettings;
      if (settings.visible && settings.renderMode !== 'mask' && !rendered.field.isEmpty) {
        const color = typeof textLayerValues.color === 'string' ? textLayerValues.color : '#f2f2f7';
        const d = contoursToPathData(rendered.field.contours, 2);
        const fill = settings.renderMode === 'stroke' ? 'none' : color;
        const stroke =
          settings.renderMode === 'stroke' || settings.renderMode === 'fillStroke'
            ? ` stroke="${color}" stroke-width="${settings.strokeWidth}" stroke-linejoin="round"`
            : '';
        parts.push(open, `<path fill="${fill}" fill-rule="nonzero"${stroke} d="${d}"/>`, '</g>');
      }
      continue;
    }

    const technique = getTechnique(layer.techniqueId);
    const entry = rendered.states.get(layer.id);
    if (!technique || !entry) continue;

    if (!technique.toSVG) {
      rasterOnly.push(technique.name);
      continue;
    }
    parts.push(open, technique.toSVG(entry.state, entry.context), '</g>');
  }

  parts.push('</svg>');
  return { svg: parts.join('\n'), rasterOnly };
}

function backgroundSvg(doc: StudioDocument, width: number, height: number): string {
  const bg = doc.background;
  const kind = typeof bg.kind === 'string' ? bg.kind : 'solid';
  const colorA = typeof bg.colorA === 'string' ? bg.colorA : '#0b0b0e';
  const colorB = typeof bg.colorB === 'string' ? bg.colorB : '#1c2a3a';

  if (kind === 'transparent') return '';

  if (kind === 'linear') {
    const angle = (typeof bg.angle === 'number' ? bg.angle : 45) * DEG;
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    return (
      `<defs><linearGradient id="bg" x1="${(0.5 - dx / 2).toFixed(4)}" y1="${(0.5 - dy / 2).toFixed(4)}" ` +
      `x2="${(0.5 + dx / 2).toFixed(4)}" y2="${(0.5 + dy / 2).toFixed(4)}">` +
      `<stop offset="0" stop-color="${colorA}"/><stop offset="1" stop-color="${colorB}"/>` +
      `</linearGradient></defs><rect width="${width}" height="${height}" fill="url(#bg)"/>`
    );
  }

  if (kind === 'radial') {
    const center = (bg.center as { x: number; y: number }) ?? { x: 0.5, y: 0.42 };
    const r = typeof bg.radius === 'number' ? bg.radius : 0.8;
    return (
      `<defs><radialGradient id="bg" cx="${center.x}" cy="${center.y}" r="${r}">` +
      `<stop offset="0" stop-color="${colorA}"/><stop offset="1" stop-color="${colorB}"/>` +
      `</radialGradient></defs><rect width="${width}" height="${height}" fill="url(#bg)"/>`
    );
  }

  // A noise texture has no vector equivalent; the base colour is the honest fallback.
  return `<rect width="${width}" height="${height}" fill="${colorA}"/>`;
}
